import {
  deletesAfterDownload,
  dropTargetsFor,
  problemsTargetFor,
  resultsTargetFor
} from './automations'
import type {
  ClientAutomation,
  DropTarget,
  ProblemFile,
  ResultFile,
  RunSummary,
  TemplateId
} from './types'

/**
 * The contract between the hosted broker and the browser app.
 *
 * Deliberately narrower than `ClientAutomation`: the browser never receives
 * `params`, so bucket names, folder paths, and script locations stay
 * server-side. A web page is an exposed place to put a bucket name, and the
 * page has no use for one — it names an automation by job id and the broker
 * resolves the rest.
 *
 * Everything the UI actually needs from `params` — whether there is a drop
 * zone, whether there are results, what extensions are allowed — is resolved
 * here, once, on the server.
 */
export interface WebAutomation {
  jobId: string
  name: string
  templateId: TemplateId
  /** null when the scheduler job could not be read. */
  paused: boolean | null
  schedule?: string
  acceptsFiles: boolean
  /** True when this automation writes downloadable output. */
  producesResults: boolean
  /** True when this automation sets aside files it cannot process. */
  setsFilesAside: boolean
  /**
   * True when downloading a result deletes it and the file it came from. The
   * browser needs this to warn before the download, not to carry it out — the
   * deleting happens on the server, which is also the only side that knows
   * which input a result came from.
   */
  deletesAfterDownload: boolean
  /**
   * One entry per file input, in the order the administrator arranged them.
   * Empty when the automation takes no files.
   *
   * Bucket and prefix are stripped: the page renders a labelled drop zone and
   * sends back the slot id, and the broker resolves where that actually goes.
   */
  drops: WebDropTarget[]
}

/** A file input as the browser sees it — no bucket, no folder path. */
export type WebDropTarget = Omit<DropTarget, 'bucket' | 'prefix'>

/** Projects a discovered automation into what the browser is allowed to know. */
export function toWebAutomation(a: ClientAutomation): WebAutomation {
  const drops = dropTargetsFor(a.templateId, a.params)
  return {
    jobId: a.jobId,
    name: a.name,
    templateId: a.templateId,
    paused: a.paused,
    ...(a.schedule ? { schedule: a.schedule } : {}),
    acceptsFiles: drops.length > 0,
    producesResults: resultsTargetFor(a.templateId, a.params) !== null,
    setsFilesAside: problemsTargetFor(a.templateId, a.params) !== null,
    deletesAfterDownload: deletesAfterDownload(a.templateId, a.params),
    drops: drops.map(({ bucket: _b, prefix: _p, ...rest }) => rest)
  }
}

/**
 * Strips an output's recorded provenance before it goes to the browser.
 *
 * The page never needs to know which input a result came from — the server
 * works that out again when the download is confirmed — and sending it would
 * put the archive folder's path in a place it has no reason to be.
 */
export function toWebResultFile(f: ResultFile): ResultFile {
  const { sourceObject: _omitted, ...rest } = f
  return rest
}

/** Who is signed in, as reported by `GET /api/session`. */
export interface SessionInfo {
  email: string
  /** The company this user belongs to. Absent for an owner, who belongs to none. */
  company?: string
}

/**
 * Permission to upload one file, minted after the server has checked its name
 * and extension and resolved where it goes.
 *
 * This is a V4 signed *POST policy*, not a signed PUT, because a policy can
 * carry conditions Cloud Storage enforces on our behalf — the size range and
 * the content type — where a PUT URL would take whatever the browser sent. The
 * browser posts `fields` plus the file as multipart/form-data to `url`.
 */
export type UploadTicket =
  | { name: string; ok: true; url: string; fields: Record<string, string>; object: string }
  | { name: string; ok: false; error: string }

/** A short-lived signed URL for downloading one result or set-aside file. */
export interface DownloadTicket {
  url: string
  name: string
}

/** Error body returned by every failing API route. */
export interface ApiErrorBody {
  error: string
  /** Present when the failure means the session is gone. */
  code?: 'auth-expired'
}

export interface AutomationsResponse {
  automations: WebAutomation[]
}
export interface RunsResponse {
  runs: RunSummary[]
}
export interface ResultsResponse {
  files: ResultFile[]
}
export interface ProblemsResponse {
  files: ProblemFile[]
}
export interface UploadsResponse {
  tickets: UploadTicket[]
}
export interface ExplanationResponse {
  text: string
}

/** Caps mirrored in the browser so oversized files fail before any round trip. */
export const MAX_FILES_PER_SEND = 20
export const MAX_FILE_BYTES = 32 * 1024 * 1024
